import { useCallback, useState } from 'react'

import { useConfirm } from '@/utils/confirm'
import { useGetErrorMessage } from '@/utils/error'
import type { ResponseError } from '@/utils/error'
import { useT } from '@/utils/language'
import { PlanType } from '@/utils/plan'
import { useRequest } from '@/utils/request'
import { useIkasaId } from '@/utils/setting'
import {
  bindSpotPayment,
  removePlanPaymentBindRedirect,
  shouldBindSpotPayment,
} from '@/utils/spotPayment'
import { useToast } from '@/utils/toast'
import type { User } from '@/utils/user'

import Page from '@/components/Page'
import PlanCard from '@/components/PlanCard'
import SubscriptionPlanModal from '@/components/SubscriptionPlanModal'

import { useNavigate } from 'react-router-dom'
import styles from './PlanPage.module.css'

function PlanPage() {
  const T = useT()
  const navigate = useNavigate()
  const toast = useToast()
  const getErrorMessage = useGetErrorMessage()
  const request = useRequest()
  const ikasaId = useIkasaId()
  const completeOrientation = useCallback(async () => {
    try {
      await request(`/service/v1/setting/user/${ikasaId}`, {
        method: 'PUT',
        showToast: false,
        data: {
          orientation_completed_at: new Date().toISOString(),
        },
      })
      return true
    } catch (e) {
      toast(getErrorMessage(e as ResponseError), 'error')
      return false
    }
  }, [ikasaId, request, toast, getErrorMessage])

  const selectPlan = useCallback(
    async (plan: PlanType) => {
      if (plan === PlanType.Office) {
        navigate('/plan/office')
        return
      }

      // HACK: ikasa must change plan type enum string
      const planType = plan === PlanType.Normal ? 'charge' : plan
      const planRequestOptions = {
        method: 'POST' as const,
        showToast: false,
        data: { ikasaId, planType },
      }

      // 現在と同じプランの申し込みは送らない。サブスク加入中に subsc を送ると
      // 解約予約として登録されるため (aikasa/ikasa_platform#7734)
      let isSamePlan: boolean
      try {
        const user = await request<User>(
          `/service/v1/setting/user/${ikasaId}`,
          { method: 'GET', showToast: false }
        )
        isSamePlan = user.planType === plan
      } catch (e) {
        toast(getErrorMessage(e as ResponseError), 'error')
        return
      }

      try {
        const { alreadyRegistered: isPaymentRegistered } = await request<{
          alreadyRegistered: boolean
        }>(`/service/v1/payment/spot/confirm/${ikasaId}`, {
          method: 'GET',
          showToast: false,
        })

        if (!isPaymentRegistered) {
          const isPaymentBound = await bindSpotPayment({
            toast,
            savePlanRedirect: true,
          })
          if (!isPaymentBound) return
        }
      } catch (e) {
        if (!shouldBindSpotPayment(e)) {
          toast(getErrorMessage(e as ResponseError), 'error')
          return
        }

        const isPaymentBound = await bindSpotPayment({
          toast,
          savePlanRedirect: true,
        })
        if (!isPaymentBound) return
      }

      if (!isSamePlan) {
        try {
          await request('/service/v1/setting/user/plan', planRequestOptions)
        } catch (e) {
          if (!shouldBindSpotPayment(e)) {
            toast(getErrorMessage(e as ResponseError), 'error')
            return
          }

          const isPaymentBound = await bindSpotPayment({
            toast,
            savePlanRedirect: true,
          })
          if (!isPaymentBound) return

          try {
            await request('/service/v1/setting/user/plan', planRequestOptions)
          } catch (e) {
            if (!shouldBindSpotPayment(e)) {
              toast(getErrorMessage(e as ResponseError), 'error')
            }
            return
          }
        }
      }

      const isOrientationCompleted = await completeOrientation()
      if (!isOrientationCompleted) return
      removePlanPaymentBindRedirect()
      navigate('/', { replace: true })
    },
    [navigate, ikasaId, request, toast, getErrorMessage, completeOrientation]
  )

  // subscription plan
  const [showSubPlanModal, setShowSubPlanModal] = useState(false)
  const onSubPlanModalConfirm = useCallback(() => {
    selectPlan(PlanType.Subscription)
  }, [selectPlan])

  // normal plan
  const confirm = useConfirm()
  const onNormalPlanClick = useCallback(async () => {
    await confirm({
      title: T('useNormalPlanConfirm'),
      content: T('useNormalPlanConfirmHint'),
    })
    selectPlan(PlanType.Normal)
  }, [T, confirm, selectPlan])

  // office plan
  const onOfficePlanClick = useCallback(async () => {
    selectPlan(PlanType.Office)
  }, [selectPlan])

  return (
    <Page className={styles.page} title={T('selectPlan')}>
      <div className={styles.title}>{T('pleaseSelectPlan')}</div>

      <PlanCard
        className={styles.card}
        type={PlanType.Subscription}
        onClick={() => setShowSubPlanModal(true)}
      />

      <PlanCard
        className={styles.card}
        type={PlanType.Normal}
        onClick={onNormalPlanClick}
      />

      <PlanCard
        className={styles.card}
        type={PlanType.Office}
        onClick={onOfficePlanClick}
      />

      <SubscriptionPlanModal
        visible={showSubPlanModal}
        onConfirm={onSubPlanModalConfirm}
        onBack={() => {
          setShowSubPlanModal(false)
        }}
      />
    </Page>
  )
}

export default PlanPage
